/* eslint-disable react/no-danger */
/* eslint-disable import/no-duplicates */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { Link, useParams } from 'react-router-dom';
import { addHours, format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import {
  FaEnvelope,
  FaFacebookF,
  FaLink,
  FaTelegramPlane,
  FaWhatsapp,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

import { Banner, Circle, Container, ShareBox } from './styles';
import Carousel from '~/components/Carousel';
import ButtonBack from '~/components/ButtonBack';
import {
  KrBoxes,
  KrCalendar,
  KrHanger,
  KrMapMark,
} from '~/components/KoroIcons';

import share from '~/assets/icons/share.svg';
import api from '~/services/api';
import Toast from '~/utils/toast';
import { useMarketing } from '~/hooks/Marketing';
import { trackViewItem } from '~/utils/ga4';

interface IParams {
  slug: string;
  preview: string;
}

interface IArchive {
  id: number;
  name: string;
  archive_url: string;
}

interface IArchiveEvent {
  id: number;
  archive: IArchive;
}

interface IModalityEvent {
  modality: {
    id: number;
    name: string;
  };
}

interface IBatchCategory {
  modality: {
    id: number;
    name: string;
  };
}

interface ISize {
  id: number;
  size: string;
  quantity?: string;
  active: boolean;
}

interface IFileKit {
  id?: number;
  name: string;
  archive_url: string;
  size?: number;
  file?: File;
}

interface ICardKit {
  id: number;
  kit_id?: number;
  description: string;
  link: string;
  archive: IFileKit;
}

interface IQuantity {
  id: number;

  size: string;
}

interface IKit {
  images: ICardKit[];
  description: string;
  quantities: IQuantity[];
  has_size_variation: boolean;
}

interface ISponsorEvent {
  id: number;
  description: string;
  link: string;
  archive: {
    id: number;
    name: string;
    archive_url: string;
  };
  sponsor: {
    id: number;
    name: string;
  };
}

interface IEventResponse {
  id: number;
  name: string;
  description: string;
  date: string;
  hour: string;
  slug: string;
  selling: number;
  archivesEvents: IArchiveEvent[];
  type: {
    id: number;
    name: string;
  };
  status: {
    name: string;
  };
  address: {
    full_address: string;
  };
  organizer: {
    company: {
      document: string;
      name: string;
      support_email: string;
      address: {
        street: string;
        number: string;
        neighborhood: string;
        city: string;
        state: string;
        country: string;
        complement?: string;
        zip_code: string;
      };
    };
  };
  kit_enabled: boolean;
  kit2: IKit;
  modalitiesEvents: IModalityEvent[];
  batchesCategories: IBatchCategory[];
  sponsorsEvents: ISponsorEvent[];
}

interface IEvent {
  name: string;
  description: string;
  date: string;
  hour: string;
  selling: number;
  type: {
    id: number;
    name: string;
  };
  status: {
    name: string;
  };
  address: {
    full_address: string;
  };
  archivesEvents: IArchiveEvent[];
  kit_enabled: boolean;
  kit2: IKit;
  modalitiesEvents: IModalityEvent[];
  batchesCategories: IBatchCategory[];
}

interface ICard {
  id: number;
  name: string;
  text: string;
  img: string;
  link: string;
}

interface ISize {
  id: number;
  size: string;
  quantity?: string;
  active: boolean;
}

const Event: React.FC = () => {
  const { setEventId } = useMarketing();
  const shareBtnRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const params = useParams<IParams>();
  const [aboutOrganizer, setAboutOrganizer] = useState(false);
  const [aboutKoro, setAboutKoro] = useState(false);
  const [event, setEvent] = useState({} as IEvent);
  const [showShare, setShowShare] = useState(false);
  const [seeMore, setSeeMore] = useState(true);
  const [cardsSponsors, setCardsSponsors] = useState<ICard[]>([]);
  const isPreview = useMemo(() => !!params.preview, [params.preview]);
  const [nameOrganizer, setNameOrganizer] = useState('');
  const [documentOrganizer, setDocumentOrganizer] = useState('');
  const [emailOrganizer, setEmailOrganizer] = useState('');
  const [addressOrganizer, setAddressOrganizer] = useState('');
  const [cardsKit, setCardsKit] = useState<ICardKit[]>([]);
  const [sizesKit, setSizesKit] = useState<ISize[]>([
    { id: 1, size: 'PP', active: false },
    { id: 2, size: 'P', active: false },
    { id: 3, size: 'M', active: false },
    { id: 4, size: 'G', active: false },
    { id: 5, size: 'GG', active: false },
    { id: 6, size: 'XG', active: false },
    { id: 7, size: 'XXG', active: false },
  ]);

  const getUniqueModalities = useCallback(
    (batchCategories: IBatchCategory[]) => {
      return (
        batchCategories?.reduce((acc, item) => {
          if (!acc.some((mod) => mod.modality.id === item.modality.id)) {
            acc.push(item);
          }
          return acc;
        }, [] as IModalityEvent[]) || []
      );
    },
    []
  );

  useEffect(() => {
    api.get<IEventResponse>(`events/${params.slug}`).then((response) => {
      const sponsorsEvents = response.data.sponsorsEvents
        ? response.data.sponsorsEvents.map<ICard>((sponsorEvent) => ({
            id: sponsorEvent.id,
            name: sponsorEvent.sponsor.name,
            text: sponsorEvent.description,
            img: sponsorEvent.archive.archive_url,
            link: sponsorEvent.link,
          }))
        : [];
      if (response.data.kit2) {
        setCardsKit(response.data.kit2.images);
        setSizesKit((oldState) => {
          return oldState.map((size) => {
            const sizeSelected = response.data.kit2.quantities.find(
              (quantity) => {
                return quantity.size === size.size;
              }
            );

            if (sizeSelected) {
              return {
                ...size,
                active: true,
              };
            }
            return {
              ...size,
            };
          });
        });
      }

      setNameOrganizer(response.data.organizer?.company.name);
      setDocumentOrganizer(response.data.organizer?.company.document);
      setEmailOrganizer(response.data.organizer?.company.support_email);
      setAddressOrganizer(`${
        response.data.organizer?.company.address.street
      } n˚ ${
        parseInt(response.data.organizer?.company.address.number, 10) === 0
          ? 'S/N'
          : response.data.organizer?.company.address.number
      }, ${response.data.organizer?.company.address.neighborhood} - ${
        response.data.organizer?.company.address.city
      }/
          ${response.data.organizer?.company.address.state}, CEP: ${
        response.data.organizer?.company.address.zip_code
      }`);

      setCardsSponsors(sponsorsEvents);
      setEvent({
        ...response.data,
        modalitiesEvents: getUniqueModalities(response.data.batchesCategories),
      });

      setEventId(response.data.id);

      // GA4: track view_item
      trackViewItem(
        {
          item_id: String(response.data.id),
          item_name: response.data.name,
          price: 0,
          quantity: 1,
        },
        params.slug
      );

      if (descriptionRef.current) {
        if (descriptionRef.current?.clientHeight < 200) {
          setSeeMore(false);
        }
      }

      localStorage.setItem('@Koro:event', JSON.stringify(response.data));
    });
  }, [getUniqueModalities, params.slug, setEventId]);

  const address = useMemo(() => {
    if (Object.keys(event).length > 0) {
      return `${event.address.full_address}`;
    }
    return '';
  }, [event]);

  const addressMap = useMemo(() => {
    if (Object.keys(event).length > 0) {
      return `${event.address.full_address}`;
    }
    return '';
  }, [event]);

  const handleClickCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(
        `${process.env.REACT_APP_APP_URL}/eventos/${params.slug}`
      );
      Toast.fire({
        icon: 'success',
        iconColor: '#ff6900',
        title: 'Link copiado!',
      });
    } catch (error) {
      // console.error('Erro ao copiar o texto: ', error);
    }
  }, [params.slug]);

  const checkClick = useCallback((e) => {
    const { target } = e;

    if (
      boxRef.current &&
      !boxRef.current.contains(target) &&
      shareBtnRef.current &&
      !shareBtnRef.current.contains(target)
    ) {
      setShowShare(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', checkClick);
  }, [checkClick]);

  const handleSeeMore = useCallback(() => {
    const height = descriptionRef.current;
    if (height) {
      height.style.maxHeight = '100%';
      height.style.overflow = 'visible';
      setSeeMore(false);
    }
  }, []);

  return (
    <Container className="position-relative">
      {Object.keys(event).length > 0 && (
        <>
          <div>
            <ButtonBack
              href={`${process.env.PUBLIC_URL}/`}
              className={`btn-back ${isPreview ? 'pe-none' : ''}`}
            />
          </div>

          <div className="position-relative">
            <button
              ref={shareBtnRef}
              type="button"
              className="btns-banner share border-0 bg-transparent"
              onClick={() => setShowShare((state) => !state)}
            >
              <img src={share} alt="Share" />
            </button>
            <ShareBox ref={boxRef} show={showShare}>
              <div>
                <p className="fw-semibold text-center text-primary">
                  Enviar para
                </p>
              </div>
              <div className="social-medias overflow-auto">
                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className={`d-inline-block border-0 bg-transparent ${
                      isPreview ? 'pe-none' : ''
                    }`}
                    onClick={handleClickCopy}
                    disabled={isPreview}
                  >
                    <Circle color="#2D76F8">
                      <FaLink size={24} color="#fff" />
                    </Circle>
                  </button>
                  <a
                    href={`https://api.whatsapp.com/send/?text=${process.env.REACT_APP_APP_URL}/eventos/${params.slug}`}
                    className={`d-inline-block border-0 bg-transparent ${
                      isPreview ? 'pe-none' : ''
                    }`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Circle color="#29D168">
                      <FaWhatsapp size={24} color="#fff" />
                    </Circle>
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?display=popup&sdk=joey&u=${process.env.REACT_APP_APP_URL}/eventos/${params.slug}`}
                    className={`d-inline-block border-0 bg-transparent ${
                      isPreview ? 'pe-none' : ''
                    }`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Circle color="#0174FB">
                      <FaFacebookF size={24} color="#fff" />
                    </Circle>
                  </a>
                  <a
                    href={`https://t.me/share/url?url=${process.env.REACT_APP_APP_URL}/eventos/${params.slug}`}
                    className={`d-inline-block border-0 bg-transparent ${
                      isPreview ? 'pe-none' : ''
                    }`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Circle color="#2EA5D9">
                      <FaTelegramPlane size={24} color="#fff" />
                    </Circle>
                  </a>
                  <a
                    href={`https://twitter.com/intent/post?url=${process.env.REACT_APP_APP_URL}/eventos/${params.slug}`}
                    className={`d-inline-block border-0 bg-transparent ${
                      isPreview ? 'pe-none' : ''
                    }`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Circle color="#000000">
                      <FaXTwitter size={24} color="#fff" />
                    </Circle>
                  </a>
                  <a
                    href={`mailto:?body=${process.env.REACT_APP_APP_URL}/eventos/${params.slug}`}
                    className={`d-inline-block border-0 bg-transparent ${
                      isPreview ? 'pe-none' : ''
                    }`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Circle color="#0DBEF2">
                      <FaEnvelope size={24} color="#fff" />
                    </Circle>
                  </a>
                </div>
              </div>
            </ShareBox>
          </div>
          <Carousel className="banner-slider">
            {event.archivesEvents.map((archiveEvent, index) => (
              <Banner
                key={archiveEvent.id}
                src={archiveEvent.archive.archive_url}
                first={index === 0}
              />
            ))}
          </Carousel>

          <div className="container">
            <div className="row">
              <div className="col-12">
                <div>
                  <div className="bg-white p-3 mt-negative">
                    <div className="d-flex align-items-center justify-content-between title pb-2 mb-3 border-bottom">
                      <h1 className="mb-0">{event.name}</h1>
                      <span>{event.type.name}</span>
                    </div>
                    <p className="d-flex align-items-center date-local">
                      <KrCalendar
                        size={14}
                        color="#FF6900"
                        className="address me-1"
                      />
                      {format(addHours(parseISO(event.date), 3), 'dd/MM/yyyy')}
                      {event.hour && event.hour !== '00:00:00'
                        ? ` - ${event.hour}`
                        : ''}{' '}
                      (
                      {format(addHours(parseISO(event.date), 3), 'EEEE', {
                        locale: ptBR,
                      })}
                      )
                    </p>
                    <p className="d-flex align-items-center date-local">
                      <KrMapMark
                        size={14}
                        color="#FF6900"
                        className="address me-1"
                      />
                      {address}
                    </p>
                    <iframe
                      src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_MAPS_KEY}&q=${addressMap}&zoom=15`}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="map"
                    />

                    <div
                      ref={descriptionRef}
                      className="description-event mt-3"
                      dangerouslySetInnerHTML={{ __html: event.description }}
                    />

                    {seeMore && (
                      <div className="text-center bg-btn-see-more">
                        <button
                          type="button"
                          className="btn-see-more mt-5 px-3 py-1"
                          onClick={handleSeeMore}
                        >
                          Ler mais...
                        </button>
                      </div>
                    )}
                  </div>

                  {cardsSponsors.length > 0 && (
                    <div className="bg-white p-3 mt-3">
                      <div className="">
                        <h2 className="mb-4 d-flex align-items-center">
                          Patrocínios/ Promoções
                        </h2>
                        <p>
                          Este evento é apoiado por empresas que valorizam o
                          esporte. Clique nas imagens para descobrir mais sobre
                          elas.
                        </p>
                        <div className="row align-items-center justify-content-center">
                          {cardsSponsors.map((sponsor) => (
                            <div className="col-4 position-relative">
                              <span
                                className={`${
                                  sponsor.text ? 'tooltip-total' : ''
                                } `}
                                data-tooltip={sponsor.text}
                              >
                                <a
                                  href={sponsor.link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`${sponsor.link ? '' : 'pe-none'}`}
                                >
                                  <img
                                    key={sponsor.id}
                                    src={sponsor.img}
                                    alt={sponsor.name}
                                    title={sponsor.text}
                                    className="card-sponsor w-100"
                                  />
                                </a>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {event.kit_enabled && event.kit2 && (
                    <div className="bg-white p-3 mt-3">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <h2 className="mb-0 d-flex align-items-center">
                          <KrHanger
                            color="#292623"
                            size={20}
                            className="me-2"
                          />
                          Kits
                        </h2>
                      </div>
                      {event.kit2.description ? (
                        <div
                          className="description-event"
                          dangerouslySetInnerHTML={{
                            __html: event.kit2.description,
                          }}
                        />
                      ) : (
                        <p>
                          {' '}
                          Adquira o Kit e transforme sua participação em um
                          marco especial. Com camisetas de alta qualidade
                          disponíveis em diversos tamanhos e medalhas
                          exclusivas, você estará pronto para participar do
                          evento com estilo e conforto.
                        </p>
                      )}

                      <div className="mt-3">
                        <Carousel dots className="mb-5 carousel-kits">
                          {cardsKit.map((kit) => (
                            <div className="cursor-pointer">
                              {kit.link ? (
                                <a
                                  href={kit.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <div className="px-1">
                                    <img
                                      src={kit.archive.archive_url}
                                      alt="kit"
                                      className="rounded-3"
                                    />
                                  </div>
                                  <p className="mb-0 pt-3 px-1 px-lg-3">
                                    {kit.description}
                                  </p>
                                </a>
                              ) : (
                                <div>
                                  <div className="px-1">
                                    <img
                                      src={kit.archive.archive_url}
                                      alt="kit"
                                      className="rounded-3"
                                    />
                                  </div>
                                  <p className="mb-0 pt-3 px-1 px-lg-3">
                                    {kit.description}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </Carousel>
                      </div>
                    </div>
                  )}

                  <div className="bg-white p-3 mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <h2 className="mb-0 d-flex align-items-center">
                        <KrBoxes color="#292623" size={20} className="me-2" />
                        Modalidade
                      </h2>
                    </div>
                    {event.modalitiesEvents.map((modalityEvent) => (
                      <div
                        key={modalityEvent.modality.id}
                        className="bd-mod d-flex align-items-center p-2"
                      >
                        <span>
                          <span className="text-black">
                            {modalityEvent.modality.name}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={`${process.env.PUBLIC_URL}/eventos/${
                      params.slug
                    }/categorias${isPreview ? '/visualizacao' : ''}`}
                    className={` btn h6 fw-normal py-2 mt-3 mb-0 w-100 ${
                      event.selling === 1 || isPreview
                        ? 'btn-login'
                        : `${
                            event.selling === 2
                              ? 'btn-suspended'
                              : 'btn-disabled'
                          }`
                    } `}
                  >
                    {event.selling === 1 || isPreview
                      ? 'Inscrever-se'
                      : `${
                          event.selling === 2
                            ? 'Inscrições Suspensas'
                            : 'Inscrições Encerradas'
                        }`}
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="bg-white px-3 pt-3 mt-3 mb-3">
                  <button
                    type="button"
                    onClick={() => setAboutOrganizer(!aboutOrganizer)}
                    className="btn-title d-flex justify-content-between align-items-center w-100 mb-3 border-0 bg-transparent"
                  >
                    Sobre o Organizador
                    <IoIosArrowDown
                      size={14}
                      color="#808080"
                      className={`${
                        aboutOrganizer ? 'rotate-arrow' : 'right-arrow'
                      }`}
                    />
                  </button>
                  <div
                    className={`organizer ${
                      aboutOrganizer
                        ? 'h-aswer-active'
                        : 'h-aswer overflow-hidden'
                    }`}
                  >
                    <p>
                      A KORO atua exclusivamente como intermediadora na venda de
                      ingressos para eventos organizados por terceiros. A
                      responsabilidade pela realização do evento, incluindo sua
                      execução, segurança, infraestrutura e cumprimento de todas
                      as normas legais, é integralmente do Organizador:
                    </p>
                    <p className="ps-4 mb-1">
                      Nome:{' '}
                      <span className="text-uppercase">{nameOrganizer}</span>
                    </p>
                    <p className="ps-4 mb-1">
                      CNPJ:{' '}
                      <span className="text-uppercase">
                        {documentOrganizer}
                      </span>
                    </p>
                    <p className="ps-4 mb-1">
                      Endereço:{' '}
                      <span className="text-uppercase">{addressOrganizer}</span>
                    </p>
                    <p className="ps-4">
                      Contato:{' '}
                      <a href={`mailto:${emailOrganizer}`}>
                        <span>{emailOrganizer}</span>
                      </a>
                      .
                    </p>

                    <p>
                      Caso o evento seja cancelado, adiado ou sofra qualquer
                      alteração, as políticas de reembolso ou compensação são
                      definidas pelo Organizador. Em caso de duvidas entre em
                      contato via email.
                    </p>

                    <p>
                      Caso o evento seja cancelado, adiado ou sofra qualquer
                      alteração, as políticas de reembolso ou compensação serão
                      definidas pelo Organizador, conforme as regras informadas
                      no momento da compra.
                    </p>

                    <p>
                      A KORO não se responsabiliza por quaisquer prejuízos
                      decorrentes dessas mudanças, sendo apenas responsável pelo
                      processamento das transações conforme as diretrizes
                      previamente estabelecidas.
                    </p>

                    <p className="pb-4">
                      Ao adquirir um ingresso através da nossa plataforma, o
                      comprador reconhece e aceita estes termos.
                    </p>
                  </div>
                </div>
                <div className="bg-white px-3 pt-3 mt-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setAboutKoro(!aboutKoro)}
                    className="btn-title d-flex justify-content-between align-items-center w-100 mb-3 border-0 bg-transparent"
                  >
                    Sobre a Koro
                    <IoIosArrowDown
                      size={14}
                      color="#808080"
                      className={`${
                        aboutKoro ? 'rotate-arrow' : 'right-arrow'
                      }`}
                    />
                  </button>
                  <div
                    className={`${
                      aboutKoro ? 'h-aswer-active' : 'h-aswer overflow-hidden'
                    }`}
                  >
                    <p>
                      A Koro é a batida do coração do esporte, um lugar onde
                      emoção e tecnologia se encontram. Nossa paixão é o
                      esporte, e nosso compromisso é conectar você aos melhores
                      eventos esportivos com facilidade e segurança. Com uma
                      plataforma criada para atletas e apaixonados, oferecemos
                      uma experiência de compra de ingressos que é tão intuitiva
                      quanto confiável.
                    </p>
                    <p className="pb-3 mb-0">
                      Na Koro, utilizamos dados de forma inteligente para criar
                      conexões únicas, permitindo que você descubra e participe
                      de competições que realmente fazem seu coração pulsar.
                      Seja na quadra, no campo, na areia ou no sofá de casa, a
                      Koro faz com que cada momento esportivo seja uma
                      experiência compartilhada de emoção e união.
                    </p>
                  </div>
                  {/* <button
                    type="button"
                    onClick={() => setFaq(!faq)}
                    className="btn-title d-flex justify-content-between align-items-center w-100 mb-3 border-0 bg-transparent"
                  >
                    F.A.Q
                    <IoIosArrowDown
                      size={14}
                      color="#808080"
                      className={`${faq ? 'rotate-arrow' : 'right-arrow'}`}
                    />
                  </button>
                  <div
                    className={`${
                      faq ? 'h-aswer-active' : 'h-aswer overflow-hidden'
                    }`}
                  >
                    <p className="mb-0">
                      Lorem ipsum dolor sit amet consectetur adipisicing elit.
                      Sint fuga rerum doloremque, alias cupiditate laudantium
                      voluptatibus. Consequuntur dicta repellendus eum esse
                      tempore, illo ea animi? Illum nobis modi at laborum!
                    </p>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default Event;
